<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="Plans"
      subtitle="Manage subscription plans available to societies."
      :headers="headers"
      :items="plans"
      :loading="loading"
      show-search
      search-placeholder="Search plans..."
      show-add
      add-label="Add Plan"
      @add="openAdd"
    >
      <!-- Title -->
      <template v-slot:item.title="{ item }">
        <div style="display:flex;align-items:center;gap:10px;">
          <div class="ism-plan-icon">
            <v-icon size="16" color="#F26522">mdi-ticket-confirmation-outline</v-icon>
          </div>
          <span style="font-weight:600;color:var(--text-primary);">{{ item.title }}</span>
        </div>
      </template>

      <!-- Description -->
      <template v-slot:item.description="{ item }">
        <span class="ism-text-clamp ism-text-secondary">{{ item.description || '—' }}</span>
      </template>

      <!-- Duration -->
      <template v-slot:item.number_of_days="{ item }">
        <span class="ism-badge ism-badge-blue">
          {{ item.number_of_days }} days
        </span>
      </template>

      <!-- Amount -->
      <template v-slot:item.amount="{ item }">
        <span class="ism-amount-text">₹ {{ Number(item.amount).toLocaleString('en-IN') }}</span>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <PremiumActionButton
            icon="mdi-pencil-outline"
            label="Edit"
            @click="openEdit(item)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Add/Edit Modal -->
    <PremiumModalForm
      v-model="planDialog"
      :title="isEdit ? 'Edit Plan' : 'Add Plan'"
      :submitText="isEdit ? 'Update Plan' : 'Save Plan'"
      :loading="saving"
      max-width="600"
      @submit="savePlan"
    >
      <div style="display:flex;flex-direction:column;gap:16px;">
        <!-- Basic Info -->
        <div class="ism-form-section">
          <div class="ism-section-title">General Information</div>
          <v-row dense>
            <v-col cols="12">
              <PremiumInput
                v-model="plan.title"
                name="title"
                data-vv-as="Plan Title"
                label="Plan Title *"
                placeholder="e.g. Platinum"
                v-validate="'required'"
                :error="errors.first('title') || serverSideErrors.title"
              />
            </v-col>
            <v-col cols="12">
              <PremiumInput
                v-model="plan.description"
                name="description"
                label="Description"
                placeholder="Short summary of the plan"
                is-textarea
                rows="2"
                :error="errors.first('description') || serverSideErrors.description"
              />
            </v-col>
          </v-row>
        </div>

        <!-- Configuration -->
        <div class="ism-form-section">
          <div class="ism-section-title">Plan Configuration</div>
          <v-row dense>

            <v-col cols="6">
              <PremiumInput
                v-model="plan.amount"
                name="amount"
                data-vv-as="Amount"
                label="Amount (Per House) *"
                placeholder="0"
                type="number"
                v-validate="'required|numeric'"
                :error="errors.first('amount') || serverSideErrors.amount"
              />
            </v-col>
            <v-col cols="6">
              <PremiumInput
                v-model="plan.number_of_days"
                name="number_of_days"
                data-vv-as="Validity"
                label="Validity (Days) *"
                placeholder="365"
                type="number"
                v-validate="'required|numeric|min_value:1'"
                :error="errors.first('number_of_days') || serverSideErrors.number_of_days"
              />
            </v-col>
          </v-row>
        </div>

        <!-- Discounts / Tiered Pricing -->
        <div class="ism-form-section">
          <div class="ism-section-title">Tiered Pricing (Discounts %)</div>
          <div class="ism-tier-grid">
            <div class="ism-tier-row ism-tier-header">
              <span>Houses Range</span>
              <span>Discount (%)</span>
            </div>
            <div class="ism-tier-row">
              <span class="ism-tier-range">0 - 50</span>
              <PremiumInput v-model="plan.house_0_50" name="house_0_50" type="number" placeholder="0" dense hide-details />
            </div>
            <div class="ism-tier-row">
              <span class="ism-tier-range">51 - 250</span>
              <PremiumInput v-model="plan.house_51_250" name="house_51_250" type="number" placeholder="0" dense hide-details />
            </div>
            <div class="ism-tier-row">
              <span class="ism-tier-range">251 - 500</span>
              <PremiumInput v-model="plan.house_251_500" name="house_251_500" type="number" placeholder="0" dense hide-details />
            </div>
          </div>
        </div>

      </div>
    </PremiumModalForm>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import PremiumTable from '../../../common/PremiumTable.vue';
import PremiumActionButton from '../../../common/PremiumActionButton.vue';
import PremiumModalForm from '../../../common/PremiumModalForm.vue';
import PremiumInput from '../../../common/PremiumInput.vue';

export default {
  name: 'PlanList',
  components: { PremiumTable, PremiumActionButton, PremiumModalForm, PremiumInput },

  data() {
    return {
      loading:    false,
      saving:     false,
      planDialog: false,
      isEdit:     false,
      editId:     null,
      plan: {
        title:          '',
        description:    '',
        amount:         '',
        number_of_days: '',
        type:           'societyadmin',
        identifier:     'year',
        house_0_50:     0,
        house_51_250:   0,
        house_251_500:  0,
      },
      serverSideErrors: {},
      plans:   [],

      headers: [
        { text: 'Plan',        value: 'title',          width: '40%' },
        { text: 'Duration',    value: 'number_of_days', width: '25%' },
        { text: 'Amount',      value: 'amount',         width: '25%' },
        { text: 'Actions',     value: 'actions',        width: '10%', sortable: false },
      ],
    };
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    getPlans() {
      this.loading = true;
      this.$axios.get('superadmin/plan').then(res => {
        this.loading = false;
        if (res.data.status) this.plans = res.data.plans;
        else this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
      }).catch(() => { this.loading = false; });
    },



    openAdd() {
      this.isEdit = false;
      this.plan = {
        title: '', description: '', amount: '', number_of_days: '',
        type: 'societyadmin', identifier: 'year',
        house_0_50: 0, house_51_250: 0, house_251_500: 0,
      };
      this.serverSideErrors = {};
      this.$validator.reset();
      this.planDialog = true;
    },

    openEdit(item) {
      this.isEdit = true;
      this.editId = item.id;
      this.plan = JSON.parse(JSON.stringify(item));
      // Ensure defaults if fields are null from old data
      this.serverSideErrors = {};
      this.$validator.reset();
      this.planDialog = true;
    },

    savePlan() {
      this.$validator.validateAll().then(valid => {
        if (!valid) return;
        this.saving = true;
        const method = this.isEdit ? 'put' : 'post';
        const url    = this.isEdit ? `superadmin/plan/${this.editId}` : 'superadmin/plan';

        this.$axios[method](url, this.plan).then(res => {
          this.saving = false;
          if (res.data.status) {
            this.planDialog = false;
            this.getPlans();
            this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: res.data.message });
          } else {
            if (res.data.errors) {
              res.data.errors.forEach(err => { this.serverSideErrors[err.field] = err.message; });
            } else {
              this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
            }
          }
        }).catch(() => { this.saving = false; });
      });
    },
  },

  mounted() {
    this.getPlans();
  },
};
</script>

<style scoped>
.ism-plan-icon {
  width: 30px; height: 30px; border-radius: 8px;
  background: rgba(242, 101, 34, 0.1);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ism-text-clamp {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden; font-size: 13px; line-height: 1.5;
}
.ism-text-secondary { color: var(--text-secondary, #64748b) !important; }
.ism-amount-text {
  font-size: 14px; font-weight: 700; color: var(--text-primary, #1e293b);
  font-family: "Inter", sans-serif;
}

.ism-form-section {
  padding: 16px; background: var(--bg-primary); border-radius: 12px;
  border: 1px solid var(--border-color);
}
.ism-section-title {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: var(--text-secondary);
  margin-bottom: 12px; border-bottom: 1px solid var(--border-color);
  padding-bottom: 6px;
}

.ism-tier-grid { display: flex; flex-direction: column; gap: 8px; }
.ism-tier-row {
  display: grid; grid-template-columns: 1fr 120px; align-items: center;
  gap: 16px; padding: 4px 12px;
}
.ism-tier-header {
  font-size: 11px; font-weight: 700; color: var(--text-secondary);
  text-transform: uppercase;
}
.ism-tier-range { font-size: 13px; font-weight: 600; color: var(--text-primary); }
</style>
